import { useState, type FormEvent } from 'react';
import { z } from 'zod';
import { updateSubscriptionState } from '../../lib/customers-api';
import { consoleZoneLabel, zoneByCode } from '../../lib/zones';
import { useToast } from '../../components/ToastProvider';
import { actionErrorToast, actionWarningToast } from '../../lib/friendly-error';
import {
  canDoDestructiveSubscriptionAction,
  type StaffRole,
} from '../../hooks/useStaffMe';

/**
 * Subscription state change modal (3c, ADR-013).
 *
 * What's shown depends on the current state and caller's role:
 *   - Mutating role (super_admin / nexa_admin) → buttons for each allowed
 *     transition from the current state per ADR-013's transition table
 *   - Any lower role (incl. support) → no transitions: the live Cedar model
 *     (ADR-075) denies support all subscription transitions
 *   - Cancel requires typed business-name confirmation (destructive + terminal)
 *   - Suspend / cancel / past_due require a 5–500 char reason
 *   - Reactivate is a single button click (no reason required)
 */

type SubState = 'active' | 'past_due' | 'suspended' | 'cancelled';

interface Props {
  accountId: string;
  businessName: string;
  currentState: SubState;
  role: StaffRole;
  /** ADR-090 — open the modal pre-targeted at a transition (the console's
   *  Activate / Deactivate / Cancel quick buttons use this to jump straight to
   *  the confirm step). Ignored if the target isn't an allowed transition for
   *  the current state + caller role. */
  initialTarget?: SubState;
  onClose: () => void;
  onUpdated: () => Promise<void> | void;
  /** D9 — the account's own zone code. The change is sent THERE and the title names it. */
  zone?: string;
  /**
   * How many of the account's domains have NOT followed its state — for `cancelled`, domains not
   * archived; for `suspended`, domains still active. Above zero, the modal offers "Finish", which
   * repeats the change so the server re-runs its (idempotent) domain cascade (ADR-013 Am.1).
   */
  domainsBehind?: number;
}

/** The action as the operator pressed it — what an error toast names. */
const ACTION_NAMES: Record<SubState, string> = {
  active: 'Reactivate subscription',
  past_due: 'Mark subscription past due',
  suspended: 'Suspend subscription',
  cancelled: 'Cancel subscription',
};
/** What a landed change is called — what a success toast names. */
const DONE_NAMES: Record<SubState, string> = {
  active: 'Subscription reactivated',
  past_due: 'Subscription marked past due',
  suspended: 'Subscription suspended',
  cancelled: 'Subscription cancelled',
};

const ReasonSchema = z.string().min(5, '5 chars minimum').max(500, '500 chars maximum');

const TRANSITIONS: Record<SubState, SubState[]> = {
  active: ['past_due', 'suspended', 'cancelled'],
  past_due: ['active', 'suspended', 'cancelled'],
  suspended: ['active', 'cancelled'],
  cancelled: [],
};

export function SubscriptionStateModal({
  accountId,
  businessName,
  currentState,
  role,
  initialTarget,
  onClose,
  onUpdated,
  zone,
  domainsBehind = 0,
}: Props) {
  const toast = useToast();
  const zoneLabel = consoleZoneLabel(zoneByCode(zone)!.code);
  // Visible targets. Subscription transitions are mutating-tier only — the LIVE
  // Cedar model (ADR-075) denies `support` ALL subscription transitions
  // (confirmed by live probe), so support gets no targets. The pre-Cedar
  // ADR-013 support-reactivate split is not granted by the deployed policy.
  const allowed = canDoDestructiveSubscriptionAction(role) ? [...TRANSITIONS[currentState]] : [];

  // ADR-090 — honour a pre-targeted open (console quick buttons) only when the
  // requested transition is actually allowed for this state + role.
  // ADR-013 Am.1 — a cancelled / suspended account whose domains did not follow (a cascade that
  // failed after the state change landed) can be FINISHED: repeating the same state re-runs the
  // cascade. The only same-state target ever offered, and only when there is something to finish.
  const canFinish =
    canDoDestructiveSubscriptionAction(role) &&
    (currentState === 'cancelled' || currentState === 'suspended') &&
    domainsBehind > 0;
  const [finishing, setFinishing] = useState(false);
  const seedTarget = initialTarget && allowed.includes(initialTarget) ? initialTarget : null;
  const [target, setTarget] = useState<SubState | null>(seedTarget);
  const [reason, setReason] = useState('');
  const [confirmName, setConfirmName] = useState('');
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  function destinationLabel(s: SubState): string {
    switch (s) {
      case 'active': return 'Reactivate';
      case 'past_due': return 'Mark past due';
      case 'suspended': return 'Suspend';
      case 'cancelled': return 'Cancel (terminal)';
    }
  }

  async function submit(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (!target) return;
    setFieldError(null);

    if (target === 'suspended' || target === 'cancelled') {
      const parsed = ReasonSchema.safeParse(reason);
      if (!parsed.success) {
        setFieldError(parsed.error.issues[0]?.message ?? 'Invalid reason');
        return;
      }
    }
    if (target === 'cancelled' && confirmName.trim() !== businessName.trim()) {
      setFieldError('Typed business name must match exactly.');
      return;
    }

    setBusy(true);
    await send(target, target === 'active' ? undefined : reason.trim());
  }

  /** Send the change to the account's own zone and report what happened — all of it. */
  async function send(state: SubState, why: string | undefined): Promise<void> {
    const ctx = { subject: businessName, zoneLabel };
    try {
      const res = await updateSubscriptionState(
        accountId,
        why === undefined ? { state } : { state, reason: why },
        zoneByCode(zone),
      );
      if (res.warnings && res.warnings.length > 0) {
        // The change LANDED; a step after it did not. Say both — never "could not change".
        toast.push({
          variant: 'error',
          ...actionWarningToast(res.warnings, {
            ...ctx,
            action: ACTION_NAMES[state],
            done: DONE_NAMES[state],
            requestId: res.requestId,
          }),
        });
      } else {
        const n = res.cascadedDomains ?? 0;
        toast.push({
          variant: 'success',
          title: `${DONE_NAMES[state]} — ${businessName} · ${zoneLabel}`,
          ...(n > 0 ? { detail: `${n} domain${n === 1 ? '' : 's'} updated to match.` } : {}),
        });
      }
      await onUpdated();
      onClose();
    } catch (err) {
      // What failed, where, why, and a reference (Jeff, 6 October 2026: a vague toast is a defect).
      toast.push({ variant: 'error', ...actionErrorToast(err, { ...ctx, action: ACTION_NAMES[state] }) });
    } finally {
      setBusy(false);
      setFinishing(false);
    }
  }

  async function finish(): Promise<void> {
    setBusy(true);
    setFinishing(true);
    await send(currentState, `Finishing the earlier ${currentState === 'cancelled' ? 'cancellation' : 'suspension'} — ${domainsBehind} domain(s) had not followed`);
  }

  return (
    <div
      className="portal-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label={`Change subscription state in ${zoneLabel}`}
      onClick={(e) => {
        // Backdrop click dismisses (per Portal UX policy).
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      <div className="portal-modal">
        <button
          type="button"
          className="portal-modal-close"
          onClick={onClose}
          disabled={busy}
          aria-label="Close"
        >
          ×
        </button>
        <h2>{target === 'cancelled' ? 'Cancel subscription' : 'Change subscription state'} in {zoneLabel}</h2>
        <p className="portal-help">
          Current state: <code>{currentState}</code> for <strong>{businessName}</strong>.
        </p>
        {currentState === 'cancelled' && (
          <p className="portal-help">
            Cancelled is terminal — no transitions out. Re-onboarding creates a new account record.
          </p>
        )}
        {canFinish && !target && (
          <div className="portal-modal-finish" role="status">
            <p>
              <strong>
                {domainsBehind} domain{domainsBehind === 1 ? ' is' : 's are'} still{' '}
                {currentState === 'cancelled' ? 'live' : 'active'}
              </strong>{' '}
              — the {currentState === 'cancelled' ? 'cancellation' : 'suspension'} landed but its
              domains did not follow. Finishing repeats it in {zoneLabel}; it is safe to repeat.
            </p>
            <button
              type="button"
              className={currentState === 'cancelled' ? 'portal-btn portal-button-danger' : 'portal-btn'}
              disabled={busy}
              onClick={() => void finish()}
            >
              {finishing ? 'Finishing…' : `Finish ${currentState === 'cancelled' ? 'cancellation' : 'suspension'}`}
            </button>
          </div>
        )}

        {!target ? (
          <div className="portal-modal-actions">
            {allowed.length === 0 ? (
              <p className="portal-help">
                {canDoDestructiveSubscriptionAction(role)
                  ? 'No other change is possible from this state.'
                  : 'No transitions available for your role from this state.'}
              </p>
            ) : (
              allowed.map((s) => (
                <button
                  key={s}
                  type="button"
                  className={s === 'cancelled' ? 'portal-btn portal-button-danger' : 'portal-btn'}
                  onClick={() => setTarget(s)}
                >
                  {destinationLabel(s)}
                </button>
              ))
            )}
            <button type="button" onClick={onClose} className="portal-link-button-secondary">Close</button>
          </div>
        ) : (
          <form onSubmit={submit} aria-busy={busy} className="portal-wizard-form" noValidate>
            <p>
              Transitioning <code>{currentState}</code> → <code>{target}</code>.
            </p>

            {(target === 'suspended' || target === 'cancelled' || target === 'past_due') && (
              <label>
                Reason {target === 'past_due' ? '(optional)' : '(5–500 chars)'}
                <textarea
                  required={target !== 'past_due'}
                  minLength={target === 'past_due' ? 0 : 5}
                  maxLength={500}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                />
              </label>
            )}

            {target === 'cancelled' && (
              <label>
                Type the business name to confirm cancel
                <input
                  required
                  value={confirmName}
                  onChange={(e) => setConfirmName(e.target.value)}
                  placeholder={businessName}
                />
              </label>
            )}

            {fieldError && <span className="portal-field-error">{fieldError}</span>}

            <div className="portal-wizard-actions">
              <button type="submit" disabled={busy} className={target === 'cancelled' ? 'portal-btn portal-button-danger' : 'portal-btn'}>
                {busy ? 'Submitting…' : `Confirm ${destinationLabel(target)}`}
              </button>
              <button
                type="button"
                onClick={() => { setTarget(null); setReason(''); setConfirmName(''); setFieldError(null); }}
                disabled={busy}
                className="portal-link-button-secondary"
              >
                ← Back
              </button>
              <button
                type="button"
                onClick={onClose}
                disabled={busy}
                className="portal-link-button-secondary"
              >
                Cancel
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
